import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';

const card  = { background: '#fff', border: '1px solid #d8e4dd', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 12px rgba(24,47,38,0.07)' };
const muted = { color: '#61726b', fontSize: '13px' };
const h2s   = { margin: 0, color: '#064734', fontSize: '16px', fontWeight: 800 };
const inp   = { border: '1px solid #d8e4dd', borderRadius: '8px', padding: '8px 10px', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '13px' };
const lbl   = { display: 'block', fontWeight: 700, color: '#334155', fontSize: '11px', marginBottom: '4px' };
const euro  = v => v != null ? Number(v).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '–';
const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('de-DE') : '';

function Btn({ color, onClick, disabled, children, small }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: color, color: '#fff', border: 'none', borderRadius: '8px',
      padding: small ? '7px 10px' : '9px 14px', cursor: disabled ? 'not-allowed' : 'pointer',
      fontWeight: 700, fontSize: small ? '12px' : '13px', opacity: disabled ? 0.6 : 1,
    }}>{children}</button>
  );
}

function Notice({ tone, children }) {
  const red = tone === 'red';
  return (
    <div style={{ background: red ? '#fff1ef' : '#dff3e9', color: red ? '#9f1d12' : '#064734', border: `1px solid ${red ? '#ffc9c2' : '#a7d9c2'}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontWeight: 700 }}>
      {children}
    </div>
  );
}

const KSTATUS_CFG = {
  vorgemerkt: { label: 'Kündigung vorgemerkt', color: '#a16207' },
  vorbereitet: { label: 'Brief vorbereitet',   color: '#1d4ed8' },
  versendet:   { label: 'Kündigung versendet', color: '#b42318' },
  bestaetigt:  { label: 'Kündigung bestätigt', color: '#064734' },
  beendet:     { label: 'Vertrag beendet',     color: '#475569' },
};

const REMINDER_REASONS = [
  'Kündigungstermin läuft ab',
  'Kündigung absenden',
  'Kündigungsbestätigung prüfen',
  'Preiserhöhung prüfen',
  'Widerrufsrecht prüfen',
  'Vertragsende bestätigen',
  'Sonstiges',
];

// ── Details section ───────────────────────────────────────────────────────────

function LifecycleSection({ rs, onSaved }) {
  const [f, setF] = useState({
    vertragsbeginn: rs.vertragsbeginn || '',
    mindestlaufzeit_monate: rs.mindestlaufzeit_monate != null ? String(rs.mindestlaufzeit_monate) : '',
    verlaengerungszeitraum_monate: rs.verlaengerungszeitraum_monate != null ? String(rs.verlaengerungszeitraum_monate) : '',
    kuendigungsfrist_monate: rs.kuendigungsfrist_monate != null ? String(rs.kuendigungsfrist_monate) : '',
    vertragsende_bestaetigt: rs.vertragsende_bestaetigt || '',
    kuendigungstermin_bestaetigt: rs.kuendigungstermin_bestaetigt || '',
    kuendigungsstatus: rs.kuendigungsstatus || '',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function save() {
    setBusy(true); setMsg('');
    try {
      const body = {
        vertragsbeginn: f.vertragsbeginn || null,
        mindestlaufzeit_monate: f.mindestlaufzeit_monate ? parseInt(f.mindestlaufzeit_monate, 10) : null,
        verlaengerungszeitraum_monate: f.verlaengerungszeitraum_monate ? parseInt(f.verlaengerungszeitraum_monate, 10) : null,
        kuendigungsfrist_monate: f.kuendigungsfrist_monate ? parseInt(f.kuendigungsfrist_monate, 10) : null,
        vertragsende_bestaetigt: f.vertragsende_bestaetigt || null,
        kuendigungstermin_bestaetigt: f.kuendigungstermin_bestaetigt || null,
        kuendigungsstatus: f.kuendigungsstatus || null,
      };
      await api.put(`/contracts/${rs.id}/details`, body);
      setMsg('Gespeichert.');
      onSaved();
    } catch (e) { setMsg(e.response?.data?.error || 'Fehler'); }
    finally { setBusy(false); }
  }

  const ktBerechnet = rs.kuendigungstermin_berechnet;
  const ktConf = rs.kuendigungstermin_confidence;

  return (
    <div style={card}>
      <h2 style={{ ...h2s, marginBottom: '12px' }}>Vertragsdaten & Fristen</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '12px' }}>
        <div>
          <span style={lbl}>Vertragsbeginn</span>
          <input type="date" style={inp} value={f.vertragsbeginn} onChange={e => setF(p => ({ ...p, vertragsbeginn: e.target.value }))} />
        </div>
        <div>
          <span style={lbl}>Mindestlaufzeit (Monate)</span>
          <input type="number" min="1" style={inp} value={f.mindestlaufzeit_monate} onChange={e => setF(p => ({ ...p, mindestlaufzeit_monate: e.target.value }))} placeholder="z.B. 24" />
        </div>
        <div>
          <span style={lbl}>Verlängerungszeitraum (Monate)</span>
          <input type="number" min="1" style={inp} value={f.verlaengerungszeitraum_monate} onChange={e => setF(p => ({ ...p, verlaengerungszeitraum_monate: e.target.value }))} placeholder="z.B. 12" />
        </div>
        <div>
          <span style={lbl}>Kündigungsfrist (Monate)</span>
          <input type="number" min="1" style={inp} value={f.kuendigungsfrist_monate} onChange={e => setF(p => ({ ...p, kuendigungsfrist_monate: e.target.value }))} placeholder="z.B. 3" />
        </div>
        <div>
          <span style={lbl}>Kündigen bis (bestätigt)</span>
          <input type="date" style={inp} value={f.kuendigungstermin_bestaetigt} onChange={e => setF(p => ({ ...p, kuendigungstermin_bestaetigt: e.target.value }))} />
        </div>
        <div>
          <span style={lbl}>Vertragsende (bestätigt)</span>
          <input type="date" style={inp} value={f.vertragsende_bestaetigt} onChange={e => setF(p => ({ ...p, vertragsende_bestaetigt: e.target.value }))} />
        </div>
        <div>
          <span style={lbl}>Kündigungsstatus</span>
          <select style={inp} value={f.kuendigungsstatus} onChange={e => setF(p => ({ ...p, kuendigungsstatus: e.target.value }))}>
            <option value="">— kein Status —</option>
            {Object.entries(KSTATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>
      {ktBerechnet && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
          <strong style={{ color: '#a16207' }}>Berechneter Kündigungstermin:</strong> {fmtDate(ktBerechnet)}{' '}
          <span style={{ ...muted, fontSize: '11px' }}>(Konfidenz: {ktConf || 'LOW'} — bitte prüfen, nicht automatisch anwenden)</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Btn color="#064734" onClick={save} disabled={busy} small>Speichern</Btn>
        {msg && <span style={{ ...muted, fontWeight: 700 }}>{msg}</span>}
      </div>
    </div>
  );
}

// ── Document Upload ───────────────────────────────────────────────────────────

function DocumentsSection({ seriesId, docs, onSaved }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [docDate, setDocDate] = useState('');
  const [notiz, setNotiz] = useState('');

  async function upload() {
    const file = fileRef.current?.files?.[0];
    if (!file) return setMsg('Bitte Datei auswählen.');
    const allowed = new Set(['application/pdf', 'image/jpeg', 'image/png']);
    if (!allowed.has(file.type)) return setMsg('Nur PDF, JPG, PNG erlaubt.');
    if (file.size > 20 * 1024 * 1024) return setMsg('Datei zu groß (max. 20 MB).');
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', 'kuendigungsbestaetigung');
      if (docDate) fd.append('doc_date', docDate);
      if (notiz) fd.append('notiz', notiz);
      await api.post(`/contracts/${seriesId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg('Hochgeladen.');
      fileRef.current.value = '';
      setDocDate(''); setNotiz('');
      onSaved();
    } catch (e) { setMsg(e.response?.data?.error || 'Upload-Fehler'); }
    finally { setBusy(false); }
  }

  async function del(docId) {
    if (!window.confirm('Dokument löschen?')) return;
    try { await api.delete(`/contracts/documents/${docId}`); onSaved(); }
    catch { setMsg('Löschen fehlgeschlagen'); }
  }

  return (
    <div style={card}>
      <h2 style={{ ...h2s, marginBottom: '10px' }}>Kündigungsbestätigung</h2>
      {docs.length === 0 && <p style={muted}>Noch keine Kündigungsbestätigung hochgeladen.</p>}
      {docs.map(d => (
        <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eef4f1', paddingBottom: '6px', marginBottom: '6px' }}>
          <div>
            <span style={{ fontWeight: 700, color: '#12231d', fontSize: '13px' }}>{d.originalname}</span>
            {d.doc_date && <span style={{ ...muted, marginLeft: '8px' }}>Datum: {fmtDate(d.doc_date)}</span>}
            {d.notiz && <div style={muted}>{d.notiz}</div>}
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Btn color="#1d4ed8" small onClick={() => window.open(`/api/contracts/documents/${d.id}/file`, '_blank')}>Öffnen</Btn>
            <Btn color="#b42318" small onClick={() => del(d.id)}>Löschen</Btn>
          </div>
        </div>
      ))}
      <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px', alignItems: 'end' }}>
        <div>
          <span style={lbl}>Datei (PDF, JPG, PNG)</span>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" ref={fileRef} style={{ fontSize: '13px', width: '100%' }} />
        </div>
        <div>
          <span style={lbl}>Dokumentdatum</span>
          <input type="date" style={inp} value={docDate} onChange={e => setDocDate(e.target.value)} />
        </div>
        <div>
          <span style={lbl}>Notiz</span>
          <input type="text" style={inp} value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z.B. Bestätigung per E-Mail" maxLength={200} />
        </div>
        <div>
          <Btn color="#064734" onClick={upload} disabled={busy} small>{busy ? '…' : 'Kündigungsbestätigung hochladen'}</Btn>
        </div>
      </div>
      {msg && <p style={{ ...muted, marginTop: '6px', fontWeight: 700 }}>{msg}</p>}
      <p style={{ ...muted, marginTop: '8px', fontSize: '11px' }}>Dateien werden verschlüsselt gespeichert. Kein öffentlicher Zugriff.</p>
    </div>
  );
}

// ── Reminders ─────────────────────────────────────────────────────────────────

function RemindersSection({ seriesId, reminders, onSaved }) {
  const [due, setDue] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!due || !reason) return setMsg('Datum und Grund pflichtfeld.');
    setBusy(true); setMsg('');
    try {
      await api.post(`/contracts/${seriesId}/reminders`, { due_date: due, reason, note: note || undefined });
      setDue(''); setReason(''); setNote(''); onSaved();
    } catch (e) { setMsg(e.response?.data?.error || 'Fehler'); }
    finally { setBusy(false); }
  }

  async function done(remId) {
    try { await api.put(`/contracts/reminders/${remId}`, { status: 'erledigt' }); onSaved(); }
    catch { setMsg('Fehler'); }
  }

  async function del(remId) {
    try { await api.delete(`/contracts/reminders/${remId}`); onSaved(); }
    catch { setMsg('Fehler'); }
  }

  const open = reminders.filter(r => r.status === 'offen');
  const done_ = reminders.filter(r => r.status !== 'offen');

  return (
    <div style={card}>
      <h2 style={{ ...h2s, marginBottom: '10px' }}>Wiedervorlagen & Erinnerungen</h2>
      {open.length === 0 && <p style={muted}>Keine offenen Erinnerungen.</p>}
      {open.map(r => (
        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eef4f1', paddingBottom: '8px', marginBottom: '8px' }}>
          <div>
            <span style={{ fontWeight: 800, color: '#12231d', fontSize: '13px' }}>{fmtDate(r.due_date)}</span>
            <span style={{ ...muted, marginLeft: '8px' }}>{r.reason}</span>
            {r.note && <div style={muted}>{r.note}</div>}
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Btn color="#0a6f4f" small onClick={() => done(r.id)}>Erledigt</Btn>
            <Btn color="#b42318" small onClick={() => del(r.id)}>Löschen</Btn>
          </div>
        </div>
      ))}
      {done_.length > 0 && (
        <div style={{ ...muted, marginBottom: '8px' }}>{done_.length} erledigte Erinnerung(en)</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginTop: '10px', alignItems: 'end' }}>
        <div>
          <span style={lbl}>Datum</span>
          <input type="date" style={inp} value={due} onChange={e => setDue(e.target.value)} />
        </div>
        <div>
          <span style={lbl}>Grund</span>
          <select style={inp} value={reason} onChange={e => setReason(e.target.value)}>
            <option value="">— Grund wählen —</option>
            {REMINDER_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <span style={lbl}>Notiz</span>
          <input type="text" style={inp} value={note} onChange={e => setNote(e.target.value)} placeholder="Optional" maxLength={200} />
        </div>
        <div>
          <Btn color="#1d4ed8" onClick={add} disabled={busy} small>{busy ? '…' : 'Erinnerung hinzufügen'}</Btn>
        </div>
      </div>
      {msg && <p style={{ ...muted, marginTop: '6px', fontWeight: 700 }}>{msg}</p>}
    </div>
  );
}

// ── Timeline ──────────────────────────────────────────────────────────────────

function TimelineSection({ timeline }) {
  if (!timeline.length) return null;
  const labels = {
    details_geaendert: 'Vertragsdaten geändert',
    dokument_hochgeladen: 'Dokument hochgeladen',
    dokument_geloescht: 'Dokument gelöscht',
    reminder_erstellt: 'Erinnerung erstellt',
    vertrag_beendet: 'Vertrag beendet',
    vertrag_reaktiviert: 'Vertrag reaktiviert',
  };
  return (
    <div style={card}>
      <h2 style={{ ...h2s, marginBottom: '10px' }}>Verlauf</h2>
      {timeline.slice(0, 20).map(ev => {
        let details = null;
        try { details = ev.details ? JSON.parse(ev.details) : null; } catch {}
        return (
          <div key={ev.id} style={{ display: 'flex', gap: '10px', borderBottom: '1px solid #eef4f1', paddingBottom: '6px', marginBottom: '6px' }}>
            <span style={{ ...muted, whiteSpace: 'nowrap', minWidth: 120 }}>{new Date(ev.created_at).toLocaleDateString('de-DE')}</span>
            <span style={{ fontWeight: 700, color: '#12231d', fontSize: '13px' }}>{labels[ev.event_type] || ev.event_type}</span>
            {details && <span style={muted}>{details.due_date || details.originalname || details.beendet_am || ''}</span>}
          </div>
        );
      })}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function ContractDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [beendenBusy, setBeendenBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await api.get(`/contracts/${id}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Vertrag konnte nicht geladen werden');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function beenden() {
    if (!window.confirm('Vertrag als beendet markieren? Er wird ausgeblendet, aber nie gelöscht.')) return;
    setBeendenBusy(true);
    try {
      const beendet_am = data.vertragsende_bestaetigt || new Date().toISOString().slice(0, 10);
      await api.post(`/contracts/${id}/beenden`, { beendet_am });
      nav('/contracts');
    } catch (e) { setError(e.response?.data?.error || 'Fehler'); }
    finally { setBeendenBusy(false); }
  }

  if (loading) return <div style={muted}>Lade…</div>;
  if (error) return <Notice tone="red">{error}</Notice>;
  if (!data) return null;

  const rs = data;
  const docs = data.docs || [];
  const reminders = data.reminders || [];
  const timeline = data.timeline || [];
  const kstatus = rs.kuendigungsstatus;
  const kstatusCfg = KSTATUS_CFG[kstatus] || null;

  return (
    <div>
      <button onClick={() => nav('/contracts')} style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', fontWeight: 700, fontSize: '13px', marginBottom: '14px' }}>
        ← Zurück zum Vertragsradar
      </button>

      <div style={{ marginBottom: '14px' }}>
        <h1 style={{ margin: 0, color: '#064734', fontSize: '22px', fontWeight: 900 }}>{rs.bezeichnung || rs.empfaenger}</h1>
        <p style={{ ...muted, margin: '4px 0 0' }}>
          {rs.empfaenger} · {rs.kategorie} · {euro(rs.erwarteter_betrag ? Math.abs(rs.erwarteter_betrag) : null)} / Monat
          {kstatusCfg && <span style={{ marginLeft: '8px', background: '#f8fafc', color: kstatusCfg.color, fontWeight: 700, padding: '2px 8px', borderRadius: 5, fontSize: '12px' }}>{kstatusCfg.label}</span>}
          {rs.beendet ? <span style={{ marginLeft: '8px', background: '#f1f5f9', color: '#475569', fontWeight: 700, padding: '2px 8px', borderRadius: 5, fontSize: '12px' }}>BEENDET {rs.beendet_am ? `(${fmtDate(rs.beendet_am)})` : ''}</span> : null}
        </p>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <Btn color="#b42318" onClick={() => nav('/kuendigungen')} small>→ Kündigungszentrale</Btn>
        {!rs.beendet && (
          <Btn color="#475569" onClick={beenden} disabled={beendenBusy} small>
            {beendenBusy ? '…' : 'Vertrag als beendet markieren'}
          </Btn>
        )}
      </div>

      <div style={{ display: 'grid', gap: '12px' }}>
        <LifecycleSection rs={rs} onSaved={load} />
        <DocumentsSection seriesId={id} docs={docs} onSaved={load} />
        <RemindersSection seriesId={id} reminders={reminders} onSaved={load} />
        <TimelineSection timeline={timeline} />
      </div>
    </div>
  );
}
